import React, { ChangeEvent, useContext, useState } from "react";
import ICurrency from "../../../../models/ICurrency";
import IOrder from "../../../../models/IOrder";
import ICatalog_Item from "../../../../models/ICatalog_Item";
import IOrder_Item from "../../../../models/IOrder_Item";
import OrderEntryService from "../../../../services/OrderEntryService";
import CatalogItemService from "../../../../services/CatalogItemService";
import SupplierContext from "../../../../contexts/SupplierContext";
import { PricePerUnit } from "../../../shared/PricePerUnit/PricePerUnit";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { QtyOnHandValue } from "../../../shared/QtyOnHandValue/QtyOnHandValue";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import OwButton from "../../../shared/OwButton/OwButton";
import * as _ from "lodash-es";
import OwS3ImageViewer from "../../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../../models/IImageSizeEnum";
import { IAddBulkItems } from "../../../../models/IAddBulkItems";

type Props = {
    item: IOrder_Item;
    addToOrder: (items: IAddBulkItems[]) => void;
    order: IOrder;
    prefix: string;
    currency?: ICurrency;
};

export const RecentItemsRow = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const unitOfMeasure = _.get(props.item.Catalog_Item, "Unit_Of_Measurement.name", "");

    // When warehouses are enabled, show the QoH for the order's shipping warehouse rather than the global total.
    // Only switch once the API has returned the per-warehouse maps; otherwise keep showing the global value.
    const warehouseMapsLoaded = Array.isArray(props.item.Catalog_Item?.Warehouse_Catalog_Items_Maps);
    const shippingWarehouse = warehouseMapsLoaded ? props.order.Shipping_Warehouse : undefined;
    const qtyOnHandMapItem =
        shippingWarehouse && props.item.Catalog_Item
            ? CatalogItemService.getQtyOnHandWarehouseMapItem(props.item.Catalog_Item, shippingWarehouse)
            : undefined;

    const [qty, setQty] = useState(props.item.Catalog_Item?.min_order_qty || 0);
    const [enableAdd, setEnableAdd] = useState<boolean>(false);

    const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(props.item.Catalog_Item!, supplierModel, props.order);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        if (!props.item.Catalog_Item) return;

        const { enableAdd: newEnableAdd, qty: newQty } = OrderEntryService.doInventoryValidation(
            props.item.Catalog_Item,
            Number(value),
            props.order.Order_Items,
            supplierModel,
            props.order,
            0
        );

        setEnableAdd(newEnableAdd);
        setQty(newQty);
    };

    const handleAddToCart = () => {
        if (!props.item.Catalog_Item) return;

        const itemToAdd: IAddBulkItems = {
            item: {
                catalog_item_id: props.item.Catalog_Item.id,
                item_no: 0,
                qty: qty || props.item.Catalog_Item.min_order_qty || 1,
                Catalog_Item: props.item.Catalog_Item,
            },
            kitDetails: [],
        };

        if (supplierModel.has_discounts) {
            const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(props.item.Catalog_Item, supplierModel, props.order);
            itemToAdd.item.discount_id = priceDetail.discount?.id;
        }

        props.addToOrder([itemToAdd]);
    };

    return (
        <div key={props.item.guid} className={"RecentItemsRow d-flex justify-content-between align-items-start mb-2 w-100"}>
            <div className={"d-flex align-items-center"}>
                <div className={"item-image w-5r h-5r d-flex align-items-center justify-content-center"}>
                    <OwS3ImageViewer
                        catalogItem={props.item.Catalog_Item}
                        imageIndex={IImageSizeEnum.THUMB}
                        isMicroView={true}
                        includeAllImages={true}
                        className={"mb-2 d-flex no-border"}
                        hideEmptyImage={false}
                        microViewSize={"5rem"}
                        imageSeq={0}
                    />
                </div>
                <div className={"d-flex flex-column align-items-start justify-content-start"}>
                    <div className={"me-2"}>{props.item.Catalog_Item?.sku}</div>
                    <div>
                        {supplierModel.has_map ? (
                            <PricePerUnit
                                priceDetail={{
                                    discountedPrice: props.item.Catalog_Item?.map || 0,
                                    originalPrice: props.item.Catalog_Item?.map || 0,
                                }}
                                prefix={props.prefix}
                                currency={props.currency}
                                baseCurrency={props.order.Catalog?.Currency}
                                exchangeRate={props.order.exchange_rate}
                            />
                        ) : (
                            <div>
                                <PricePerUnit
                                    priceDetail={priceDetail}
                                    prefix={props.prefix}
                                    currency={props.currency}
                                    baseCurrency={props.order.Catalog?.Currency}
                                    exchangeRate={props.order.exchange_rate}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="me-2">
                <div className={"d-flex"}>
                    <div className="input-group">
                        <OwFnInput fn={handleChange} name={"qty"} value={qty} noOuterDiv={true} />
                        <OwButton
                            className={"btn-sm btn-primary me-2"}
                            leftIconClass={"fas fa-plus"}
                            title={"add"}
                            fn={handleAddToCart}
                            disabled={!enableAdd}
                        />
                    </div>
                </div>
                <div>{unitOfMeasure}</div>
                <div className={"d-flex text-end"}>
                    {props.item.Catalog_Item?.has_inventory ? (
                        <>
                            <OwLabel title={"qoh"} postText={":"} className={"me-2"} />
                            <QtyOnHandValue
                                catalogItem={props.item.Catalog_Item}
                                mapItem={qtyOnHandMapItem}
                                shippingWarehouse={shippingWarehouse}
                            />
                        </>
                    ) : null}
                </div>
                <div className={"d-flex text-end text-small"}>
                    <OwLabel title={"qty_ordered_last"} postText={":"} /> {props.item.qty}
                </div>
            </div>
        </div>
    );
};
